import { Skeleton } from '@playbooks/molecules/skeletons';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@playbooks/ui/components/card';
import { CardMedia } from '@playbooks/ui/components/card';
import { Icon, resolveIcon } from '@playbooks/ui/components/icon';
import { cn } from '@playbooks/ui/lib/utils';
import { formatDate } from '@playbooks/utils';

import type { CardContentProps } from './types';

type DetailCardProps = Pick<
	CardContentProps,
	'className' | 'icon' | 'loading' | 'media' | 'photo' | 'pretitle' | 'subtitle' | 'text' | 'title'
> & {
	cover?: string;
	updatedAt?: string | number | Date;
};

export const DetailCard = ({
	icon,
	cover,
	photo,
	media,
	pretitle,
	title,
	subtitle,
	text,
	updatedAt,
	loading,
	className = '',
}: DetailCardProps) => {
	// Theme
	const rootClassName = cn('mb-4 overflow-hidden', className);

	// Render
	if (loading) return <Skeleton layout='detail' className={className} />;

	return (
		<Card className={rootClassName}>
			<CardHeader className='w-full aspect-[2/1]'>
				{cover ? (
					<img src={cover} alt={title + ' cover'} />
				) : (
					<div className='pointer-events-none absolute inset-0 flex-middle'>
						<CardMedia className='text-4xl'>
							<Icon type='fad' icon='image' aria-hidden />
						</CardMedia>
					</div>
				)}
			</CardHeader>
			<CardContent className='text-left'>
				{media ? (
					<div className='border rounded-control overflow-hidden -mt-12 mb-4 w-20'>{media}</div>
				) : (
					<div className='border rounded-control overflow-hidden -mt-12 mb-4 w-20'>
						{photo ? (
							<img src={photo} alt={title + ' photo'} />
						) : (
							<div className='flex-middle aspect-square w-full bg-surface'>
								<CardMedia className='flex-middle'>
									<Icon {...resolveIcon(icon, 'fad')} aria-hidden />
								</CardMedia>
							</div>
						)}
					</div>
				)}
				{pretitle && <div>{pretitle}</div>}
				{title && <CardTitle className='type-heading'>{title}</CardTitle>}
				{subtitle && <CardDescription className='line-clamp-3'>{subtitle}</CardDescription>}
				{text && <CardDescription className='line-clamp-5'>{text}</CardDescription>}
			</CardContent>
			{updatedAt && (
				<CardFooter className='text-xs text-foreground-body'>Last updated {formatDate(updatedAt)}</CardFooter>
			)}
		</Card>
	);
};

export type { DetailCardProps };
